import React, { useMemo, useRef, useState } from "react";
import Header from "./Header";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCalendarDays,
    faUsers,
    faEnvelope,
    faPhone,
    faCommentDots,
    faCircleCheck,
    faCircleXmark,
    faMapMarkedAlt,
    faWandMagicSparkles,
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "./FadeIn.css";

const CONTACT_EMAIL_ENDPOINT =
    "https://l7qmpdzu54.execute-api.us-west-1.amazonaws.com/prod/tripEmail";

const FAQ_URL = "/faq";
const TERMS_URL = "/terms-of-use";

function pad2(n) {
    return String(n).padStart(2, "0");
}

function toYMD(d) {
    if (!d) {
        return "";
    }

    const y = d.getFullYear();
    const m = pad2(d.getMonth() + 1);
    const day = pad2(d.getDate());

    return `${y}-${m}-${day}`;
}

function formatPrettyRange(startDate, endDate) {
    if (!startDate && !endDate) {
        return "";
    }

    const fmt = (d) =>
        d.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });

    if (startDate && endDate) {
        return `${fmt(startDate)} – ${fmt(endDate)}`;
    }

    if (startDate) {
        return `${fmt(startDate)} –`;
    }

    return `– ${fmt(endDate)}`;
}

function isSameDay(a, b) {
    return (
        a &&
        b &&
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

function startOfMonth(d) {
    return new Date(
        d.getFullYear(),
        d.getMonth(),
        1
    );
}

function addMonths(d, n) {
    return new Date(
        d.getFullYear(),
        d.getMonth() + n,
        1
    );
}

function daysInMonth(d) {
    return new Date(
        d.getFullYear(),
        d.getMonth() + 1,
        0
    ).getDate();
}

function clampToMidnight(d) {
    return new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
        0,
        0,
        0,
        0
    );
}

function isInRange(day, start, end) {
    if (!start || !end) {
        return false;
    }

    const t = day.getTime();

    return (
        t >= start.getTime() &&
        t <= end.getTime()
    );
}

function DateRangePicker({
    open,
    onClose,
    startDate,
    endDate,
    onChange,
    anchorRef,
}) {
    const panelRef = useRef(null);

    const [viewMonth, setViewMonth] =
        React.useState(() =>
            startOfMonth(new Date())
        );

    React.useEffect(() => {
        if (open) {
            const base =
                startDate || new Date();

            setViewMonth(
                startOfMonth(base)
            );
        }
    }, [open, startDate]);

    React.useEffect(() => {
        if (!open) {
            return;
        }

        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        const onPointerDown = (e) => {
            const panel =
                panelRef.current;

            const anchor =
                anchorRef?.current;

            if (!panel) {
                return;
            }

            const target =
                e.target;

            if (
                panel.contains(target)
            ) {
                return;
            }

            if (
                anchor &&
                anchor.contains(target)
            ) {
                return;
            }

            onClose();
        };

        window.addEventListener(
            "keydown",
            onKeyDown
        );

        window.addEventListener(
            "mousedown",
            onPointerDown
        );

        window.addEventListener(
            "touchstart",
            onPointerDown
        );

        return () => {
            window.removeEventListener(
                "keydown",
                onKeyDown
            );

            window.removeEventListener(
                "mousedown",
                onPointerDown
            );

            window.removeEventListener(
                "touchstart",
                onPointerDown
            );
        };
    }, [
        open,
        onClose,
        anchorRef,
    ]);

    if (!open) {
        return null;
    }

    const monthLabel =
        viewMonth.toLocaleDateString(
            undefined,
            {
                month: "long",
                year: "numeric",
            }
        );

    const firstDay =
        new Date(
            viewMonth.getFullYear(),
            viewMonth.getMonth(),
            1
        );

    const offset =
        firstDay.getDay();

    const dim =
        daysInMonth(viewMonth);

    const cells = [];

    for (
        let i = 0;
        i < offset;
        i++
    ) {
        cells.push(null);
    }

    for (
        let day = 1;
        day <= dim;
        day++
    ) {
        cells.push(
            new Date(
                viewMonth.getFullYear(),
                viewMonth.getMonth(),
                day
            )
        );
    }

    while (
        cells.length % 7 !== 0
    ) {
        cells.push(null);
    }

    const weekDays = [
        "Sun",
        "Mon",
        "Tue",
        "Wed",
        "Thu",
        "Fri",
        "Sat",
    ];

    const handleDayClick = (d) => {
        if (!d) {
            return;
        }

        const clicked =
            clampToMidnight(d);

        if (
            !startDate ||
            (
                startDate &&
                endDate
            )
        ) {
            onChange(
                clicked,
                null
            );

            return;
        }

        const s =
            clampToMidnight(
                startDate
            );

        if (
            clicked.getTime() <
            s.getTime()
        ) {
            onChange(
                clicked,
                null
            );

            return;
        }

        onChange(
            s,
            clicked
        );

        onClose();
    };

    const isSelectedStart = (d) =>
        startDate &&
        isSameDay(
            d,
            startDate
        );

    const isSelectedEnd = (d) =>
        endDate &&
        isSameDay(
            d,
            endDate
        );

    const startMid =
        startDate
            ? clampToMidnight(
                  startDate
              )
            : null;

    const endMid =
        endDate
            ? clampToMidnight(
                  endDate
              )
            : null;

    return (
        <div className="relative">
            <div
                ref={panelRef}
                className="absolute z-50 mt-2 w-[320px] rounded-lg border border-gray-200 bg-white shadow-lg p-3"
                style={{
                    left: 0,
                }}
            >
                <div className="flex items-center justify-between mb-2">
                    <button
                        type="button"
                        onClick={() =>
                            setViewMonth(
                                (m) =>
                                    addMonths(
                                        m,
                                        -1
                                    )
                            )
                        }
                        className="p-2 rounded-md hover:bg-gray-100 text-gray-700"
                        aria-label="Previous month"
                    >
                        <FontAwesomeIcon
                            icon={
                                faChevronLeft
                            }
                        />
                    </button>

                    <div className="font-extrabold text-gray-900">
                        {monthLabel}
                    </div>

                    <button
                        type="button"
                        onClick={() =>
                            setViewMonth(
                                (m) =>
                                    addMonths(
                                        m,
                                        1
                                    )
                            )
                        }
                        className="p-2 rounded-md hover:bg-gray-100 text-gray-700"
                        aria-label="Next month"
                    >
                        <FontAwesomeIcon
                            icon={
                                faChevronRight
                            }
                        />
                    </button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-xs text-gray-500 mb-1">
                    {weekDays.map(
                        (w) => (
                            <div
                                key={w}
                                className="text-center py-1 font-semibold"
                            >
                                {w}
                            </div>
                        )
                    )}
                </div>

                <div className="grid grid-cols-7 gap-1">
                    {cells.map(
                        (
                            d,
                            idx
                        ) => {
                            if (!d) {
                                return (
                                    <div
                                        key={`empty-${idx}`}
                                        className="h-9"
                                    />
                                );
                            }

                            const inRange =
                                isInRange(
                                    clampToMidnight(
                                        d
                                    ),
                                    startMid,
                                    endMid
                                );

                            const startSel =
                                isSelectedStart(
                                    d
                                );

                            const endSel =
                                isSelectedEnd(
                                    d
                                );

                            const base =
                                "h-9 w-full rounded-md text-sm font-semibold flex items-center justify-center transition select-none";

                            const normal =
                                "hover:bg-gray-100 text-gray-800";

                            const rangeClass =
                                inRange
                                    ? "bg-red-50 text-gray-900"
                                    : "";

                            const startClass =
                                startSel
                                    ? "bg-[#b42520] text-white hover:bg-[#941c18]"
                                    : "";

                            const endClass =
                                endSel
                                    ? "bg-[#b42520] text-white hover:bg-[#941c18]"
                                    : "";

                            return (
                                <button
                                    key={toYMD(
                                        d
                                    )}
                                    type="button"
                                    onClick={() =>
                                        handleDayClick(
                                            d
                                        )
                                    }
                                    className={`${base} ${normal} ${rangeClass} ${startClass} ${endClass}`}
                                >
                                    {d.getDate()}
                                </button>
                            );
                        }
                    )}
                </div>

                <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="text-xs text-gray-600">
                        {startDate &&
                        !endDate
                            ? "Select an end date."
                            : "Select a start and end date."}
                    </div>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                onChange(
                                    null,
                                    null
                                );
                            }}
                            className="px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 font-semibold text-sm"
                        >
                            Clear
                        </button>

                        <button
                            type="button"
                            onClick={
                                onClose
                            }
                            className="px-3 py-2 rounded-md bg-[#b42520] text-white hover:bg-[#941c18] font-extrabold text-sm"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Book() {
    const [
        isSubmitting,
        setIsSubmitting,
    ] = React.useState(false);

    const [
        status,
        setStatus,
    ] = React.useState("idle");

    const [
        message,
        setMessage,
    ] = React.useState("");

    const [
        form,
        setForm,
    ] = React.useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        visitDates: "",
        partySize: "",
        preferredContact: "text",
        interests: "",
        otherNotes: "",
        Country: "",
        agreeToTerms: false,
    });

    const [
        tripStart,
        setTripStart,
    ] = React.useState(null);

    const [
        tripEnd,
        setTripEnd,
    ] = React.useState(null);

    const [
        isDatePickerOpen,
        setIsDatePickerOpen,
    ] = React.useState(false);

    const dateInputWrapperRef =
        useRef(null);

    const [
        fieldErrors,
        setFieldErrors,
    ] = React.useState({});

    const [
        attemptedSubmit,
        setAttemptedSubmit,
    ] = React.useState(false);

    const fieldRefs =
        useRef({
            firstName: null,
            lastName: null,
            email: null,
            phone: null,
            visitDates: null,
            partySize: null,
            interests: null,
            agreeToTerms: null,
        });

    const requiredFields =
        useMemo(
            () => [
                {
                    key: "firstName",
                    label: "First Name",
                },
                {
                    key: "lastName",
                    label: "Last Name",
                },
                {
                    key: "email",
                    label: "Email",
                },
                {
                    key: "phone",
                    label: "Phone Number",
                },
                {
                    key: "visitDates",
                    label: "Trip Dates",
                },
                {
                    key: "partySize",
                    label: "Party Size",
                },
                {
                    key: "preferredContact",
                    label: "Preferred Contact",
                },
                {
                    key: "interests",
                    label: "Interests",
                },
                {
                    key: "agreeToTerms",
                    label: "Terms of Use",
                },
            ],
            []
        );

    const validateEmail =
        (value) => {
            const v =
                value.trim();

            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                v
            );
        };

    const validateRequired =
        () => {
            const errs = {};
            const missingLabels =
                [];

            for (
                const f of
                requiredFields
            ) {
                if (
                    f.key ===
                    "visitDates"
                ) {
                    const ok =
                        Boolean(
                            tripStart &&
                                tripEnd &&
                                form.visitDates.trim()
                        );

                    if (!ok) {
                        errs.visitDates =
                            "Please select your trip start and end dates.";

                        missingLabels.push(
                            f.label
                        );
                    }

                    continue;
                }

                if (
                    f.key ===
                    "preferredContact"
                ) {
                    const ok =
                        Boolean(
                            String(
                                form.preferredContact ||
                                    ""
                            ).trim()
                        );

                    if (!ok) {
                        errs.preferredContact =
                            "Please choose how you'd like to be contacted.";

                        missingLabels.push(
                            f.label
                        );
                    }

                    continue;
                }

                if (
                    f.key ===
                    "agreeToTerms"
                ) {
                    const ok =
                        Boolean(
                            form.agreeToTerms ===
                                true
                        );

                    if (!ok) {
                        errs.agreeToTerms =
                            "You must agree to the Jackpot Concierge Terms of Use to continue.";

                        missingLabels.push(
                            f.label
                        );
                    }

                    continue;
                }

                const value =
                    String(
                        form[
                            f.key
                        ] ?? ""
                    ).trim();

                if (!value) {
                    errs[
                        f.key
                    ] =
                        `Please enter your ${f.label.toLowerCase()}.`;

                    missingLabels.push(
                        f.label
                    );
                }
            }

            if (
                !errs.email &&
                form.email.trim() &&
                !validateEmail(
                    form.email
                )
            ) {
                errs.email =
                    "Please enter a valid email address.";
            }

            if (
                !errs.partySize &&
                String(
                    form.partySize
                ).trim()
            ) {
                const partySizeInt =
                    parseInt(
                        form.partySize,
                        10
                    );

                if (
                    Number.isNaN(
                        partySizeInt
                    ) ||
                    partySizeInt <=
                        0
                ) {
                    errs.partySize =
                        "Party size must be a positive number.";
                }
            }

            return {
                errs,
                missingLabels,
            };
        };

    const scrollToFirstError =
        (errs) => {
            const order = [
                "firstName",
                "lastName",
                "email",
                "phone",
                "visitDates",
                "partySize",
                "preferredContact",
                "interests",
                "agreeToTerms",
            ];

            for (
                const k of
                order
            ) {
                if (!errs[k]) {
                    continue;
                }

                if (
                    k ===
                    "preferredContact"
                ) {
                    const el =
                        document.getElementById(
                            "preferred-contact-section"
                        );

                    if (el) {
                        el.scrollIntoView(
                            {
                                behavior:
                                    "smooth",
                                block:
                                    "center",
                            }
                        );
                    }

                    return;
                }

                if (
                    k ===
                    "agreeToTerms"
                ) {
                    const el =
                        document.getElementById(
                            "agree-to-terms-section"
                        );

                    if (el) {
                        el.scrollIntoView(
                            {
                                behavior:
                                    "smooth",
                                block:
                                    "center",
                            }
                        );
                    }

                    return;
                }

                const refEl =
                    fieldRefs
                        .current?.[
                        k
                    ];

                if (
                    refEl &&
                    typeof refEl.scrollIntoView ===
                        "function"
                ) {
                    refEl.scrollIntoView(
                        {
                            behavior:
                                "smooth",
                            block:
                                "center",
                        }
                    );

                    try {
                        refEl.focus();
                    } catch (e) {
                        console.error(
                            e
                        );
                    }

                    return;
                }

                const fallback =
                    document.getElementById(
                        k
                    );

                if (
                    fallback &&
                    typeof fallback.scrollIntoView ===
                        "function"
                ) {
                    fallback.scrollIntoView(
                        {
                            behavior:
                                "smooth",
                            block:
                                "center",
                        }
                    );

                    return;
                }
            }
        };

    const canSubmit =
        useMemo(
            () =>
                !isSubmitting,
            [
                isSubmitting,
            ]
        );

    const update =
        (key) => (e) => {
            setStatus(
                "idle"
            );

            setMessage("");

            const value =
                e?.target
                    ?.type ===
                "checkbox"
                    ? e.target
                          .checked
                    : e.target
                          .value;

            setForm(
                (prev) => ({
                    ...prev,
                    [key]:
                        value,
                })
            );

            if (
                attemptedSubmit
            ) {
                setFieldErrors(
                    (prev) => {
                        const next = {
                            ...prev,
                        };

                        delete next[
                            key
                        ];

                        return next;
                    }
                );
            }
        };

    const handleDateRangeChange =
        (
            start,
            end
        ) => {
            setTripStart(
                start
            );

            setTripEnd(
                end
            );

            const pretty =
                formatPrettyRange(
                    start,
                    end
                );

            setForm(
                (prev) => ({
                    ...prev,
                    visitDates:
                        pretty,
                })
            );

            setStatus(
                "idle"
            );

            setMessage("");

            if (
                attemptedSubmit
            ) {
                setFieldErrors(
                    (prev) => {
                        const next = {
                            ...prev,
                        };

                        delete next.visitDates;

                        return next;
                    }
                );
            }
        };

    const sendQuoteRequest =
        async (
            partySizeInt
        ) => {
            const additionalInfo =
                `Trip Dates: ${form.visitDates.trim()}\n` +
                `Start Date: ${toYMD(tripStart)}\n` +
                `End Date: ${toYMD(tripEnd)}\n` +
                `Party Size: ${partySizeInt}\n` +
                `Preferred Contact: ${form.preferredContact}\n` +
                `Interests: ${form.interests.trim()}\n` +
                `Other Notes: ${form.otherNotes.trim() || "None"}`;

            const response =
                await fetch(
                    CONTACT_EMAIL_ENDPOINT,
                    {
                        method:
                            "POST",

                        headers: {
                            "Content-Type":
                                "application/json",
                        },

                        body:
                            JSON.stringify(
                                {
                                    firstName:
                                        form.firstName.trim(),

                                    lastName:
                                        form.lastName.trim(),

                                    email:
                                        form.email.trim(),

                                    phone:
                                        form.phone.trim(),

                                    visitDates:
                                        form.visitDates.trim(),

                                    startDate:
                                        toYMD(
                                            tripStart
                                        ),

                                    endDate:
                                        toYMD(
                                            tripEnd
                                        ),

                                    partySize:
                                        partySizeInt,

                                    preferredContact:
                                        form.preferredContact,

                                    interests:
                                        form.interests.trim(),

                                    otherNotes:
                                        form.otherNotes.trim(),

                                    additionalInfo,
                                }
                            ),
                    }
                );

            if (
                !response.ok
            ) {
                const text =
                    await response
                        .text()
                        .catch(
                            () =>
                                ""
                        );

                throw new Error(
                    text ||
                        "Failed to send quote request."
                );
            }
        };

    const handleSubmit =
        async (
            event
        ) => {
            event.preventDefault();

            if (
                form.Country
            ) {
                return;
            }

            setAttemptedSubmit(
                true
            );

            setStatus(
                "idle"
            );

            setMessage("");

            const {
                errs,
                missingLabels,
            } =
                validateRequired();

            if (
                Object.keys(
                    errs
                ).length >
                0
            ) {
                setFieldErrors(
                    errs
                );

                const combined =
                    [
                        ...new Set(
                            missingLabels
                        ),
                    ];

                setStatus(
                    "error"
                );

                setMessage(
                    combined.length >
                        0
                        ? `Please fill out: ${combined.join(
                              ", "
                          )}.`
                        : "Please fix the highlighted fields and try again."
                );

                scrollToFirstError(
                    errs
                );

                return;
            }

            setIsSubmitting(
                true
            );

            try {
                const partySizeInt =
                    parseInt(
                        form.partySize,
                        10
                    );

                await sendQuoteRequest(
                    partySizeInt
                );

                setStatus(
                    "success"
                );

                setMessage(
                    "Your quote request has been sent. A Jackpot Concierge trip planner will reach out to you within 24 hours."
                );

                setFieldErrors(
                    {}
                );

                setAttemptedSubmit(
                    false
                );

                setForm({
                    firstName:
                        "",
                    lastName:
                        "",
                    email:
                        "",
                    phone:
                        "",
                    visitDates:
                        "",
                    partySize:
                        "",
                    preferredContact:
                        "text",
                    interests:
                        "",
                    otherNotes:
                        "",
                    Country:
                        "",
                    agreeToTerms:
                        false,
                });

                setTripStart(
                    null
                );

                setTripEnd(
                    null
                );
            } catch (err) {
                console.error(
                    err
                );

                setStatus(
                    "error"
                );

                setMessage(
                    err?.message ||
                        "An error occurred while sending your quote request. Please try again."
                );
            } finally {
                setIsSubmitting(
                    false
                );
            }
        };

    const showError =
        (key) =>
            attemptedSubmit &&
            Boolean(
                fieldErrors?.[
                    key
                ]
            );

    const inputBaseClass =
        "mt-2 w-full rounded-md border bg-gray-50 px-4 py-2 text-gray-900 outline-none focus:ring-2 focus:ring-[#b42520]";

    const inputErrorClass =
        "border-red-500 ring-1 ring-red-300 focus:ring-red-400";

    const inputOkClass =
        "border-gray-300";

    return (
        <div className="min-h-screen flex flex-col">
            <Helmet>
                <title>
                    Jackpot VIP Quote | Jackpot Concierge
                </title>

                <meta
                    name="description"
                    content="Request a personalized quote for Las Vegas trip planning from Jackpot Concierge."
                />
            </Helmet>

            <Header />

            <div className="flex-grow bg-[#f3f4f6] py-6">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="mt-6 flex flex-col items-center text-center">
                        <div className="inline-flex items-center gap-3">
                            <FontAwesomeIcon
                                icon={
                                    faMapMarkedAlt
                                }
                                className="text-[#b42520] text-2xl"
                            />

                            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">
                                Personalized Las Vegas Trip Planning
                            </h1>
                        </div>

                        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                            <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm text-sm text-gray-700">
                                <FontAwesomeIcon
                                    icon={
                                        faWandMagicSparkles
                                    }
                                    className="text-[#b42520]"
                                />

                                Custom itinerary + local gems
                            </div>

                            <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm text-sm text-gray-700">
                                <FontAwesomeIcon
                                    icon={
                                        faCommentDots
                                    }
                                    className="text-[#b42520]"
                                />

                                Planner contact within 24 hours
                            </div>

                            <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm text-sm text-gray-700">
                                <FontAwesomeIcon
                                    icon={
                                        faEnvelope
                                    }
                                    className="text-[#b42520]"
                                />

                                Free quote request
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 flex flex-wrap max-w-7xl w-full">
                        <div className="w-full lg:w-1/2">
                            <div
                                className="bg-white rounded-lg shadow-md p-6"
                                style={{
                                    animation:
                                        "fadeInUp 0.5s ease-out both",
                                }}
                            >
                                <div className="text-center">
                                    <span className="text-2xl font-extrabold text-gray-900">
                                        Request a Jackpot VIP Quote
                                    </span>

                                    <div className="mt-2 text-gray-600">
                                        Tell us about your Las Vegas trip and we’ll contact you with a personalized quote.
                                    </div>
                                </div>

                                <form
                                    onSubmit={
                                        handleSubmit
                                    }
                                    className="mt-6"
                                    noValidate
                                >
                                    <div
                                        style={{
                                            display:
                                                "none",
                                        }}
                                    >
                                        <input
                                            type="text"
                                            name="Country"
                                            value={
                                                form.Country
                                            }
                                            onChange={update(
                                                "Country"
                                            )}
                                            autoComplete="off"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label
                                                className="block text-sm font-medium text-gray-700"
                                                htmlFor="grid-first-name"
                                            >
                                                First Name{" "}
                                                <span className="text-red-600">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                id="grid-first-name"
                                                type="text"
                                                placeholder="John"
                                                value={
                                                    form.firstName
                                                }
                                                onChange={update(
                                                    "firstName"
                                                )}
                                                ref={(
                                                    el
                                                ) =>
                                                    (fieldRefs.current.firstName =
                                                        el)
                                                }
                                                aria-invalid={showError(
                                                    "firstName"
                                                )}
                                                className={`${inputBaseClass} ${
                                                    showError(
                                                        "firstName"
                                                    )
                                                        ? inputErrorClass
                                                        : inputOkClass
                                                }`}
                                            />

                                            {showError(
                                                "firstName"
                                            ) && (
                                                <div className="mt-1 text-xs text-red-600 font-semibold">
                                                    {
                                                        fieldErrors.firstName
                                                    }
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label
                                                className="block text-sm font-medium text-gray-700"
                                                htmlFor="grid-last-name"
                                            >
                                                Last Name{" "}
                                                <span className="text-red-600">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                id="grid-last-name"
                                                type="text"
                                                placeholder="Doe"
                                                value={
                                                    form.lastName
                                                }
                                                onChange={update(
                                                    "lastName"
                                                )}
                                                ref={(
                                                    el
                                                ) =>
                                                    (fieldRefs.current.lastName =
                                                        el)
                                                }
                                                aria-invalid={showError(
                                                    "lastName"
                                                )}
                                                className={`${inputBaseClass} ${
                                                    showError(
                                                        "lastName"
                                                    )
                                                        ? inputErrorClass
                                                        : inputOkClass
                                                }`}
                                            />

                                            {showError(
                                                "lastName"
                                            ) && (
                                                <div className="mt-1 text-xs text-red-600 font-semibold">
                                                    {
                                                        fieldErrors.lastName
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                        <div>
                                            <label
                                                className="block text-sm font-medium text-gray-700"
                                                htmlFor="grid-email"
                                            >
                                                Email{" "}
                                                <span className="text-red-600">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                id="grid-email"
                                                type="email"
                                                placeholder="email@example.com"
                                                value={
                                                    form.email
                                                }
                                                onChange={update(
                                                    "email"
                                                )}
                                                ref={(
                                                    el
                                                ) =>
                                                    (fieldRefs.current.email =
                                                        el)
                                                }
                                                aria-invalid={showError(
                                                    "email"
                                                )}
                                                className={`${inputBaseClass} ${
                                                    showError(
                                                        "email"
                                                    )
                                                        ? inputErrorClass
                                                        : inputOkClass
                                                }`}
                                            />

                                            {showError(
                                                "email"
                                            ) && (
                                                <div className="mt-1 text-xs text-red-600 font-semibold">
                                                    {
                                                        fieldErrors.email
                                                    }
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label
                                                className="block text-sm font-medium text-gray-700"
                                                htmlFor="grid-phone"
                                            >
                                                Phone Number{" "}
                                                <span className="text-red-600">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                id="grid-phone"
                                                type="tel"
                                                placeholder="123-456-7890"
                                                value={
                                                    form.phone
                                                }
                                                onChange={update(
                                                    "phone"
                                                )}
                                                ref={(
                                                    el
                                                ) =>
                                                    (fieldRefs.current.phone =
                                                        el)
                                                }
                                                aria-invalid={showError(
                                                    "phone"
                                                )}
                                                className={`${inputBaseClass} ${
                                                    showError(
                                                        "phone"
                                                    )
                                                        ? inputErrorClass
                                                        : inputOkClass
                                                }`}
                                            />

                                            {showError(
                                                "phone"
                                            ) && (
                                                <div className="mt-1 text-xs text-red-600 font-semibold">
                                                    {
                                                        fieldErrors.phone
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <label
                                            className="block text-sm font-medium text-gray-700"
                                            htmlFor="visit-dates"
                                        >
                                            When will you be visiting Las Vegas?{" "}
                                            <span className="text-red-600">
                                                *
                                            </span>
                                        </label>

                                        <div
                                            className="relative mt-2"
                                            ref={
                                                dateInputWrapperRef
                                            }
                                        >
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setIsDatePickerOpen(
                                                        (
                                                            v
                                                        ) =>
                                                            !v
                                                    )
                                                }
                                                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-gray-100 text-gray-600"
                                                aria-label="Select trip dates"
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        faCalendarDays
                                                    }
                                                />
                                            </button>

                                            <input
                                                id="visit-dates"
                                                type="text"
                                                readOnly
                                                placeholder="Select your trip dates"
                                                value={
                                                    form.visitDates
                                                }
                                                onClick={() =>
                                                    setIsDatePickerOpen(
                                                        true
                                                    )
                                                }
                                                ref={(
                                                    el
                                                ) =>
                                                    (fieldRefs.current.visitDates =
                                                        el)
                                                }
                                                aria-invalid={showError(
                                                    "visitDates"
                                                )}
                                                className={`w-full rounded-md border bg-gray-50 pl-12 pr-4 py-2 text-gray-900 outline-none focus:ring-2 cursor-pointer ${
                                                    showError(
                                                        "visitDates"
                                                    )
                                                        ? "border-red-500 ring-1 ring-red-300 focus:ring-red-400"
                                                        : "border-gray-300 focus:ring-[#b42520]"
                                                }`}
                                            />

                                            <DateRangePicker
                                                open={
                                                    isDatePickerOpen
                                                }
                                                onClose={() =>
                                                    setIsDatePickerOpen(
                                                        false
                                                    )
                                                }
                                                startDate={
                                                    tripStart
                                                }
                                                endDate={
                                                    tripEnd
                                                }
                                                onChange={
                                                    handleDateRangeChange
                                                }
                                                anchorRef={
                                                    dateInputWrapperRef
                                                }
                                            />
                                        </div>

                                        {tripStart &&
                                            tripEnd && (
                                                <div className="mt-2 text-xs text-gray-600">
                                                    Selected:{" "}
                                                    <span className="font-semibold">
                                                        {toYMD(
                                                            tripStart
                                                        )}
                                                    </span>{" "}
                                                    to{" "}
                                                    <span className="font-semibold">
                                                        {toYMD(
                                                            tripEnd
                                                        )}
                                                    </span>
                                                </div>
                                            )}

                                        {showError(
                                            "visitDates"
                                        ) && (
                                            <div className="mt-1 text-xs text-red-600 font-semibold">
                                                {
                                                    fieldErrors.visitDates
                                                }
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-4">
                                        <label
                                            className="block text-sm font-medium text-gray-700"
                                            htmlFor="party-size"
                                        >
                                            How many people are in your party?{" "}
                                            <span className="text-red-600">
                                                *
                                            </span>
                                        </label>

                                        <div className="relative mt-2">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                                <FontAwesomeIcon
                                                    icon={
                                                        faUsers
                                                    }
                                                />
                                            </span>

                                            <input
                                                id="party-size"
                                                type="number"
                                                min="1"
                                                value={
                                                    form.partySize
                                                }
                                                onChange={update(
                                                    "partySize"
                                                )}
                                                ref={(
                                                    el
                                                ) =>
                                                    (fieldRefs.current.partySize =
                                                        el)
                                                }
                                                aria-invalid={showError(
                                                    "partySize"
                                                )}
                                                className={`w-full rounded-md border bg-gray-50 pl-10 pr-4 py-2 text-gray-900 outline-none focus:ring-2 ${
                                                    showError(
                                                        "partySize"
                                                    )
                                                        ? "border-red-500 ring-1 ring-red-300 focus:ring-red-400"
                                                        : "border-gray-300 focus:ring-[#b42520]"
                                                }`}
                                            />
                                        </div>

                                        {showError(
                                            "partySize"
                                        ) && (
                                            <div className="mt-1 text-xs text-red-600 font-semibold">
                                                {
                                                    fieldErrors.partySize
                                                }
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        className="mt-4"
                                        id="preferred-contact-section"
                                    >
                                        <label className="block text-sm font-medium text-gray-700">
                                            How would you like to be contacted?{" "}
                                            <span className="text-red-600">
                                                *
                                            </span>
                                        </label>

                                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setForm(
                                                        (
                                                            p
                                                        ) => ({
                                                            ...p,
                                                            preferredContact:
                                                                "phone",
                                                        })
                                                    );

                                                    setStatus(
                                                        "idle"
                                                    );

                                                    setMessage(
                                                        ""
                                                    );

                                                    if (
                                                        attemptedSubmit
                                                    ) {
                                                        setFieldErrors(
                                                            (
                                                                prev
                                                            ) => {
                                                                const next =
                                                                    {
                                                                        ...prev,
                                                                    };

                                                                delete next.preferredContact;

                                                                return next;
                                                            }
                                                        );
                                                    }
                                                }}
                                                className={`rounded-md border px-4 py-2 text-sm font-semibold transition ${
                                                    form.preferredContact ===
                                                    "phone"
                                                        ? "border-[#b42520] bg-[#b42520] text-white"
                                                        : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                                                }`}
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        faPhone
                                                    }
                                                    className="mr-2"
                                                />

                                                Phone Call
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setForm(
                                                        (
                                                            p
                                                        ) => ({
                                                            ...p,
                                                            preferredContact:
                                                                "email",
                                                        })
                                                    );

                                                    setStatus(
                                                        "idle"
                                                    );

                                                    setMessage(
                                                        ""
                                                    );

                                                    if (
                                                        attemptedSubmit
                                                    ) {
                                                        setFieldErrors(
                                                            (
                                                                prev
                                                            ) => {
                                                                const next =
                                                                    {
                                                                        ...prev,
                                                                    };

                                                                delete next.preferredContact;

                                                                return next;
                                                            }
                                                        );
                                                    }
                                                }}
                                                className={`rounded-md border px-4 py-2 text-sm font-semibold transition ${
                                                    form.preferredContact ===
                                                    "email"
                                                        ? "border-[#b42520] bg-[#b42520] text-white"
                                                        : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                                                }`}
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        faEnvelope
                                                    }
                                                    className="mr-2"
                                                />

                                                Email
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setForm(
                                                        (
                                                            p
                                                        ) => ({
                                                            ...p,
                                                            preferredContact:
                                                                "text",
                                                        })
                                                    );

                                                    setStatus(
                                                        "idle"
                                                    );

                                                    setMessage(
                                                        ""
                                                    );

                                                    if (
                                                        attemptedSubmit
                                                    ) {
                                                        setFieldErrors(
                                                            (
                                                                prev
                                                            ) => {
                                                                const next =
                                                                    {
                                                                        ...prev,
                                                                    };

                                                                delete next.preferredContact;

                                                                return next;
                                                            }
                                                        );
                                                    }
                                                }}
                                                className={`rounded-md border px-4 py-2 text-sm font-semibold transition ${
                                                    form.preferredContact ===
                                                    "text"
                                                        ? "border-[#b42520] bg-[#b42520] text-white"
                                                        : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                                                }`}
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        faCommentDots
                                                    }
                                                    className="mr-2"
                                                />

                                                Text
                                            </button>
                                        </div>

                                        {showError(
                                            "preferredContact"
                                        ) && (
                                            <div className="mt-1 text-xs text-red-600 font-semibold">
                                                {
                                                    fieldErrors.preferredContact
                                                }
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-4">
                                        <label
                                            className="block text-sm font-medium text-gray-700"
                                            htmlFor="interests"
                                        >
                                            Is there anything specific that interests you while you're here?{" "}
                                            <span className="text-red-600">
                                                *
                                            </span>
                                        </label>

                                        <textarea
                                            id="interests"
                                            rows="3"
                                            placeholder="Example: shows, clubs, foodie spots, pool parties, sportsbooks, speakeasies, day trips..."
                                            value={
                                                form.interests
                                            }
                                            onChange={update(
                                                "interests"
                                            )}
                                            ref={(
                                                el
                                            ) =>
                                                (fieldRefs.current.interests =
                                                    el)
                                            }
                                            aria-invalid={showError(
                                                "interests"
                                            )}
                                            className={`mt-2 w-full rounded-md border bg-gray-50 px-4 py-2 text-gray-900 outline-none focus:ring-2 ${
                                                showError(
                                                    "interests"
                                                )
                                                    ? "border-red-500 ring-1 ring-red-300 focus:ring-red-400"
                                                    : "border-gray-300 focus:ring-[#b42520]"
                                            }`}
                                        />

                                        {showError(
                                            "interests"
                                        ) && (
                                            <div className="mt-1 text-xs text-red-600 font-semibold">
                                                {
                                                    fieldErrors.interests
                                                }
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-4">
                                        <label
                                            className="block text-sm font-medium text-gray-700"
                                            htmlFor="other-notes"
                                        >
                                            Is there anything else we should know about your trip?{" "}
                                            <span className="text-gray-500">
                                                (optional)
                                            </span>
                                        </label>

                                        <textarea
                                            id="other-notes"
                                            rows="3"
                                            placeholder="Budget, hotel, must-dos, accessibility needs, celebrations, vibe you’re going for..."
                                            value={
                                                form.otherNotes
                                            }
                                            onChange={update(
                                                "otherNotes"
                                            )}
                                            className="mt-2 w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-gray-900 outline-none focus:ring-2 focus:ring-[#b42520]"
                                        />
                                    </div>

                                    <div
                                        className="mt-5"
                                        id="agree-to-terms-section"
                                    >
                                        <label className="flex items-start gap-3 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    form.agreeToTerms
                                                }
                                                onChange={update(
                                                    "agreeToTerms"
                                                )}
                                                ref={(
                                                    el
                                                ) =>
                                                    (fieldRefs.current.agreeToTerms =
                                                        el)
                                                }
                                                aria-invalid={showError(
                                                    "agreeToTerms"
                                                )}
                                                className={`mt-1 h-4 w-4 rounded border ${
                                                    showError(
                                                        "agreeToTerms"
                                                    )
                                                        ? "border-red-500"
                                                        : "border-gray-300"
                                                }`}
                                            />

                                            <span>
                                                I agree to the Jackpot Concierge{" "}
                                                <a
                                                    href={
                                                        TERMS_URL
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-extrabold text-[#b42520] hover:text-[#941c18] underline"
                                                >
                                                    Terms of Use
                                                </a>{" "}
                                                <span className="text-red-600">
                                                    *
                                                </span>
                                            </span>
                                        </label>

                                        {showError(
                                            "agreeToTerms"
                                        ) && (
                                            <div className="mt-1 text-xs text-red-600 font-semibold">
                                                {
                                                    fieldErrors.agreeToTerms
                                                }
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                                        <button
                                            type="submit"
                                            disabled={
                                                !canSubmit
                                            }
                                            className={`w-full rounded-md px-5 py-3 font-extrabold text-white transition ${
                                                canSubmit
                                                    ? "bg-[#b42520] hover:bg-[#941c18]"
                                                    : "bg-gray-400 cursor-not-allowed"
                                            }`}
                                        >
                                            {isSubmitting
                                                ? "Sending Request..."
                                                : "Request a Quote"}
                                        </button>

                                        <a
                                            href={
                                                FAQ_URL
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full sm:w-auto rounded-md px-5 py-3 font-extrabold border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 transition text-center"
                                        >
                                            FAQ
                                        </a>
                                    </div>

                                    {message && (
                                        <div className="mt-4 rounded-md border px-4 py-3">
                                            <div className="flex items-start gap-3">
                                                <div className="mt-0.5">
                                                    {status ===
                                                    "success" ? (
                                                        <FontAwesomeIcon
                                                            icon={
                                                                faCircleCheck
                                                            }
                                                            className="text-green-600"
                                                        />
                                                    ) : status ===
                                                      "error" ? (
                                                        <FontAwesomeIcon
                                                            icon={
                                                                faCircleXmark
                                                            }
                                                            className="text-red-600"
                                                        />
                                                    ) : null}
                                                </div>

                                                <div
                                                    className={`text-sm ${
                                                        status ===
                                                        "success"
                                                            ? "text-green-700"
                                                            : "text-red-700"
                                                    }`}
                                                >
                                                    {
                                                        message
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>

                        <div className="w-full lg:w-1/2 mt-10 lg:mt-0 lg:pl-12 space-y-8">
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h3 className="font-extrabold text-xl text-gray-900">
                                    What you get
                                </h3>

                                <ul className="mt-3 space-y-3 text-gray-700 list-disc list-inside">
                                    <li>
                                        Personalized trip planning based on your dates, group, interests, and preferences.
                                    </li>

                                    <li>
                                        Help planning reservations, activities, nightlife, dining, and other Las Vegas experiences.
                                    </li>

                                    <li>
                                        A personalized quote based on the trip planning services you need.
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h3 className="font-extrabold text-xl text-gray-900">
                                    How it works
                                </h3>

                                <ul className="mt-3 space-y-3 text-gray-700 list-disc list-inside">
                                    <li>
                                        Tell us your dates, party size, and what you’re interested in.
                                    </li>

                                    <li>
                                        Submit your quote request with no payment required.
                                    </li>

                                    <li>
                                        A Jackpot Concierge trip planner will contact you to discuss your trip and provide a personalized quote.
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h3 className="font-extrabold text-xl text-gray-900">
                                    Already in Las Vegas?
                                </h3>

                                <p className="mt-2 text-gray-700">
                                    For real-time tips and on-the-fly support, use the app to chat with your concierge while you’re here.
                                </p>

                                <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                                    <FontAwesomeIcon
                                        icon={
                                            faCommentDots
                                        }
                                        className="text-[#b42520]"
                                    />

                                    Faster responses through the app
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h3 className="font-extrabold text-xl text-gray-900">
                                    What happens next?
                                </h3>

                                <p className="mt-2 text-gray-700">
                                    After you submit your request, a Jackpot Concierge trip planner will review your trip details and contact you using your preferred contact method to discuss options and provide a quote.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Book;